import { HTMLAttributes } from 'react';
import { useLocation } from 'react-router-dom';
import { PaginationItem } from '@mui/material';
import MUIPagination from '@mui/material/Pagination';
import { UsePaginationProps } from '@mui/material/usePagination';
import { LeftArrow } from '../../../../lib/icons/LeftArrow';
import { RightArrow } from '../../../../lib/icons/RightArrow';
import PaginationSkeleton from './PaginationSkeleton';
import styles from './Pagination.module.scss';
import { Api } from '../../../../lib/types/apiSpec';

export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onChange: (currentPage: number) => void;
  isLoading?: boolean;
} & Omit<HTMLAttributes<HTMLElement>, 'onChange'>;

const Pagination = ({ currentPage, totalPages, onChange, isLoading, className }: PaginationProps) => {
  const { pathname, search } = useLocation();
  const handlePageChange: UsePaginationProps['onChange'] = (_event, page) => {
    _event.preventDefault();
    onChange(page);
  };

  if (totalPages < 2) {
    return null;
  }

  if (isLoading) {
    return <PaginationSkeleton />;
  }

  return (
    <MUIPagination
      count={totalPages}
      page={currentPage}
      onChange={handlePageChange}
      color="primary"
      classes={{ ul: styles.container, root: className }}
      renderItem={params => {
        const searchParams = new URLSearchParams(search);

        if (params.page) {
          searchParams.set(Api.Param.Page, params.page.toString());
        }

        const path = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

        return totalPages === 1 && (params.type === 'next' || params.type === 'previous') ? null : (
          <PaginationItem
            classes={{
              ellipsis: styles.ellipsis,
              previousNext: styles.arrows,
              root: styles.item,
              selected: styles.activeItem,
            }}
            {...params}
            href={path}
            slots={{ previous: LeftArrow, next: RightArrow }}
            component="a"
          />
        );
      }}
      shape="rounded"
    />
  );
};

export default Pagination;
